import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, Typography, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { InfoOutlined } from '@mui/icons-material';
import TermsModal from './termModal';
import api from '../api/api';
import Loading from '../components/loading';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../components/successMessage';
import ErrorMessage from '../components/errorMessage';

function TermsList() {
  const [terms, setTerms] = useState([]);
  const [openInstructions, setOpenInstructions] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [currentTerm, setCurrentTerm] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const openMenu = Boolean(anchorEl);

  const [isLoading, setLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const navigate = useNavigate();

  const getTerms = useCallback(() => {
    setLoading(true);
    api.get("/terms", {
      headers: {
        Authorization: sessionStorage.getItem('token')
      }
    }).then((returnFromApi) => {
      setTerms(returnFromApi.data);
      setLoading(false);
    }).catch((returnFromApi) => {
      setLoading(false);
      setErrorMessage(returnFromApi.response.data);
      setShowErrorMessage(true);
      console.error("ops! ocorreu um erro" + returnFromApi);
      if (returnFromApi.status === 401) {
        navigate("/expired-session");
      }
    });
  }, [navigate]);

  useEffect(() => {
    getTerms();
  }, [getTerms]);

  const menuOpen = (event, term) => {
    setAnchorEl(event.currentTarget);
    setSelectedTerm(term);
  };

  const menuClose = () => {
    setAnchorEl(null);
    setSelectedTerm(null);
  };

  const openingInstructions = () => {
    setOpenInstructions(true);
  };

  const closingInstructions = (continuar) => {
    setOpenInstructions(false);
    if (continuar) {
      openNewModal();
    }
  };

  const openNewModal = () => {
    setCurrentTerm(null);
    setOpenModal(true);
  };

  const deleteTerm = () => {
    if (!selectedTerm) return;
    deleteTermById(selectedTerm.id);
    menuClose();
  };

  const modalClose = () => {
    setOpenModal(false);
    setCurrentTerm(null);
  };

  const onSavedTerm = (returnFromApi) => {
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
    } else {
      setShowErrorMessage(true)
      setErrorMessage(returnFromApi.response.data);
    }
    modalClose();
    getTerms();
  }

  const deleteTermById = (id) => {
    setLoading(true);
    api.delete(`/terms/${id}`, {
      headers: {
        Authorization: sessionStorage.getItem('token')
      }
    }).then((returnFromApi) => {
      setLoading(false);
      setSuccessMessage(returnFromApi.data);
      setShowSuccessMessage(true);
      getTerms();
    }).catch((returnFromApi) => {
      setLoading(false);
      setErrorMessage(returnFromApi.response?.data);
      console.error("ops! ocorreu um erro" + returnFromApi);
    });
  };

  const download = () => {
    api.get(`/terms/${selectedTerm.id}/url`, {
      headers: {
        Authorization: sessionStorage.getItem('token')
      }
    }).then((returnFromApi) => {
      const url = returnFromApi.data;
      window.location.href = url;
      setLoading(false);
    }).catch((returnFromApi) => {
      setLoading(false);
      setErrorMessage("Erro ao baixar o arquivo.");
      setShowErrorMessage(true);
      console.error("ops! ocorreu um erro" + returnFromApi);
    });

    menuClose();
  };

  return (
    <Box sx={{ padding: 3 }}>

      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && <SuccessMessage message={successMessage} show={showSuccessMessage} setShow={setShowSuccessMessage} />}
      {showErrorMessage && <ErrorMessage message={errorMessage} show={showErrorMessage} setShow={setShowErrorMessage} />}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ mb: 0, fontWeight: 'bold', color: 'text.secondary' }}>
          Configuração de Termos
        </Typography>
        <Button
          variant="outlined"
          onClick={openingInstructions}
        >
          Adicionar Termo
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data de Cadastro</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {terms.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  Nenhum termo cadastrado ainda.
                </TableCell>
              </TableRow>
            ) : (
              terms.map((term) => (
                <TableRow key={term.id} hover>
                  <TableCell>{term.description}</TableCell>
                  <TableCell>{term.date}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="ações"
                      aria-controls={openMenu ? 'actions-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={openMenu ? 'true' : undefined}
                      onClick={(event) => menuOpen(event, term)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        id="actions-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={menuClose}
        MenuListProps={{
          'aria-labelledby': 'actions-button',
        }}
      >
        <MenuItem onClick={download}>
          Baixar .docx
        </MenuItem>
        <MenuItem onClick={deleteTerm} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>

      </Menu>

      <Dialog open={openInstructions} onClose={() => closingInstructions(false)}>
        <DialogTitle sx={{ fontWeight: 'bold', alignItems: 'center' }}>
          <InfoOutlined sx={{ mr: 1 }} />
          Como preparar seu modelo de documento
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography gutterBottom>
              Para que o sistema consiga gerar o termo corretamente...
            </Typography>
            <Typography sx={{ mt: 2, mb: 2 }} variant="body2" component="div">
              <strong>Exemplo:</strong>
              <Box sx={{ padding: 1, backgroundColor: 'rgba(0,0,0,0.03)', mt: 1 }}>
                O paciente [NOME_PACIENTE] está de acordo com o procedimento realizado no dia {'$DATA_CONSULTA'}.
              </Box>
            </Typography>
            <Typography gutterBottom>
              As tags serão substituídas automaticamente pelos dados reais de cada paciente.
            </Typography>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              <strong> Tags disponíveis para uso:</strong>
            </Typography>
            <ul>
              <li><Typography variant="body2" component="span">[NOME_PACIENTE]</Typography></li>
              <li><Typography variant="body2" component="span">[TELEFONE_PACIENTE]</Typography></li>
              <li><Typography variant="body2" component="span">[CPF_PACIENTE]</Typography></li>
              <li><Typography variant="body2" component="span">[DATA_CONSULTA]</Typography></li>
              <li><Typography variant="body2" component="span">[NOME_DENTISTA]</Typography></li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closingInstructions(true)} variant="contained">
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      <TermsModal
        open={openModal}
        onClose={modalClose}
        onSave={onSavedTerm}
        currentTerm={currentTerm}
      />
    </Box>
  );
}

export default TermsList;